import { loader } from "@monaco-editor/react";
import { memoize } from "lodash-es";
import { wireTmGrammars } from "monaco-editor-textmate";
import { IGrammarDefinition, Registry } from "monaco-textmate";
import { loadWASM } from "onigasm";
import { useEffect, useState } from "react";

import regoJSON from "../json/rego.json";

const initialize = memoize(
  async () => {
    try {
      await loadWASM("/onigasm.wasm");
    } catch (e) {
      // when using the dev server, we may sometimes end up calling init twice, so just ignore that error
      if (!`${e}`.includes("Onigasm#init has been called")) {
        throw e;
      }
    }

    const monaco = await loader.init();

    monaco.editor.defineTheme("light", {
      base: "vs",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#FFFFFF",
      },
    });
    monaco.editor.defineTheme("dark", {
      base: "vs-dark",
      inherit: true,
      rules: [],
      colors: {
        "editor.background": "#262626",
      },
    });
    monaco.languages.register({ id: "rego" });

    const registry = new Registry({
      async getGrammarDefinition(): Promise<IGrammarDefinition> {
        return {
          format: "json",
          content: regoJSON,
        };
      },
    });

    const languages = new Map<string, string>();
    languages.set("rego", "source.rego");

    // Expecting full monaco-editor type not the editor.api subset type.
    // Safe because wireTmGrammars only uses monaco.languages.setTokensProvider (present in both).
    await wireTmGrammars(
      monaco as typeof import("monaco-editor"),
      registry,
      languages,
    );
  },
  () => true,
);

type Result = {
  loading: boolean;
  error?: any;
};

const useMonaco = () => {
  const [result, setResult] = useState<Result>({ loading: true });

  useEffect(() => {
    (async () => {
      try {
        setResult({ loading: true });
        await initialize();
        setResult({ loading: false });
      } catch (e) {
        setResult({ loading: false, error: e });
      }
    })();
  }, []);

  return result;
};

export default useMonaco;
